import React from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ArrowRight, Ticket, Wallet, ShieldCheck, Zap, Lightbulb, Smartphone, BookOpen, Sparkles } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const HERO_IMG =
  "https://images.pexels.com/photos/8475146/pexels-photo-8475146.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SERVICES = [
  { icon: BookOpen, label: "XLearna Premium", desc: "Subscribe to learning content with voucher value." },
  { icon: Smartphone, label: "Airtime", desc: "Recharge any South African network in seconds." },
  { icon: Zap, label: "Data Bundles", desc: "Top up mobile data across providers." },
  { icon: Lightbulb, label: "Electricity", desc: "Buy prepaid electricity directly from your wallet." },
];

export default function Home() {
  return (
    <PublicShell>
      {/* HERO */}
      <section className="relative overflow-hidden fv-noise">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">
              <Sparkles size={14} /> One voucher · Many services
            </p>
            <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-bold text-[#0A2540]">
              Stored-value vouchers,
              <br />
              <span className="text-[#00C4B4]">spent your way.</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-[#333333] max-w-xl">
              FlexiVouch turns a single voucher into a flexible spending wallet.
              Top up once, redeem partially across airtime, data, electricity,
              XLearna Premium and more — until the balance is exhausted.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-row">
              <Link to="/signup">
                <Button data-testid="hero-signup-btn" className="bg-[#0A2540] hover:bg-[#133D6B] text-white rounded-full px-6 py-6 text-base font-semibold inline-flex items-center gap-2">
                  Become a Merchant <ArrowRight size={18} />
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button data-testid="hero-learn-btn" variant="outline" className="border-2 border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white rounded-full px-6 py-6 text-base font-semibold">
                  How it works
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat label="Min Expiry" value="3 yrs" />
              <Stat label="PIN Length" value="16-digit" />
              <Stat label="Partial Redemption" value="Yes" />
            </div>
          </div>

          {/* Voucher illustration */}
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={HERO_IMG}
                alt="Merchant accepting digital payment"
                className="rounded-2xl shadow-xl w-full h-[420px] object-cover"
              />
              <div className="absolute -bottom-8 -left-4 sm:-left-10 fv-voucher p-6 w-72 hidden sm:block">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#00C4B4] font-bold">Voucher</p>
                <p className="font-mono text-[#0A2540] mt-1">••••-••••-••••-3456</p>
                <p className="font-display text-3xl font-bold text-[#0A2540] mt-3">R 200.00</p>
                <p className="text-xs text-[#666666] mt-1">of R 200.00 original</p>
                <div className="mt-3 h-1.5 w-full rounded-full bg-[#F8F9FA] overflow-hidden">
                  <div className="h-full bg-[#00C4B4] w-full" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES STRIP */}
      <section className="bg-white border-y border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">Multi-utility</p>
              <h2 className="font-display mt-2 text-3xl sm:text-4xl tracking-tight font-bold text-[#0A2540]">
                One balance. Spend on what matters.
              </h2>
            </div>
            <p className="max-w-md text-sm text-[#666666]">
              FlexiVouch is built on a provider-abstraction layer — new services
              plug in without changing how vouchers work.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s) => (
              <div
                data-testid={`service-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                key={s.label}
                className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-6 hover:border-[#00C4B4] transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-[#0A2540] text-white flex items-center justify-center">
                  <s.icon size={20} />
                </div>
                <p className="font-display mt-4 text-lg font-semibold text-[#0A2540]">{s.label}</p>
                <p className="text-sm text-[#666666] mt-1 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Step n="01" icon={ShieldCheck} title="Sign up & verify" desc="Register as a merchant with email or SA cellphone. Complete KYC and get approved." />
          <Step n="02" icon={Wallet} title="Top up your wallet" desc="Deposit funds and upload proof of payment. Admin verifies and credits your wallet." />
          <Step n="03" icon={Ticket} title="Issue & redeem" desc="Generate 16-digit voucher PINs. Customers redeem partially across multiple services." />
        </div>
      </section>

      {/* CALLOUT */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="bg-[#0A2540] rounded-3xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center p-10 sm:p-14">
            <div>
              <LogoMark className="text-white [&_span]:!text-white [&_span_span]:!text-[#00C4B4]" />
              <h2 className="font-display mt-6 text-3xl sm:text-4xl tracking-tight font-bold text-white">
                Built for the South African economy.
              </h2>
              <p className="mt-4 text-white/80 max-w-md leading-relaxed">
                Designed for spaza shops, informal merchants, fintech operators
                and digital service providers. Cryptographically secure PINs,
                partial redemption and a real-time admin ledger.
              </p>
              <Link to="/signup">
                <Button data-testid="callout-signup-btn" className="mt-6 bg-[#00C4B4] hover:bg-[#009E91] text-white rounded-full px-6 py-6 text-base font-semibold">
                  Open a merchant account
                </Button>
              </Link>
            </div>
            <div className="fv-dot-grid relative rounded-2xl bg-white/5 p-10 border border-white/10">
              <div className="grid grid-cols-2 gap-5">
                <Metric label="Vouchers sold" value="∞" />
                <Metric label="Active merchants" value="∞" />
                <Metric label="Outstanding liability" value="Live" />
                <Metric label="SLAs" value="Atomic" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="font-display text-2xl font-bold text-[#0A2540]">{value}</p>
      <p className="text-xs text-[#666666] uppercase tracking-wider">{label}</p>
    </div>
  );
}

function Step({ n, icon: Icon, title, desc }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-7 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-[#999999]">{n}</span>
        <Icon size={20} className="text-[#00C4B4]" />
      </div>
      <p className="font-display mt-6 text-xl font-semibold text-[#0A2540]">{title}</p>
      <p className="text-sm text-[#666666] mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-white/8 border border-white/10 rounded-xl p-5">
      <p className="text-xs uppercase tracking-[0.18em] text-[#00C4B4] font-bold">{label}</p>
      <p className="font-display mt-2 text-3xl font-bold text-white">{value}</p>
    </div>
  );
}
